/**
 * Side-by-side comparison tables surfaced by SHOW_COMPARISON_TABLE (AP-451).
 *
 * Local types — the FE boundary lint forbids `state/` slices from
 * depending on `features/*` modules.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/state/store';

export type RangePosition = 'below' | 'above' | 'in_range' | 'unknown';

export interface ComparisonRow {
  key: string;
  name: string;
  unit: string;
  current: number | null;
  prior: number | null;
  delta: number | null;
  pct_change: number | null;
  current_in_range: RangePosition;
  prior_in_range: RangePosition;
  clinically_significant: boolean;
}

export interface ComparisonTablePayload {
  current_study_uuid: string;
  prior_study_uuid: string;
  rows: ComparisonRow[];
  count: number;
  significant_count: number;
}

export interface ComparisonTable {
  currentStudyUuid: string;
  priorStudyUuid: string;
  rows: ComparisonRow[];
  significantCount: number;
  receivedAt: number;
}

interface CopilotComparisonsState {
  byCurrentStudy: Record<string, ComparisonTable>;
  latestStudyUuid: string | null;
}

const initialState: CopilotComparisonsState = {
  byCurrentStudy: {},
  latestStudyUuid: null,
};

const slice = createSlice({
  name: 'copilotComparisons',
  initialState,
  reducers: {
    comparisonReceived: (
      state,
      action: PayloadAction<ComparisonTablePayload>
    ) => {
      const p = action.payload;
      state.byCurrentStudy[p.current_study_uuid] = {
        currentStudyUuid: p.current_study_uuid,
        priorStudyUuid: p.prior_study_uuid,
        rows: p.rows,
        significantCount: p.significant_count,
        receivedAt: Date.now(),
      };
      state.latestStudyUuid = p.current_study_uuid;
    },
    comparisonCleared: (
      state,
      action: PayloadAction<{ currentStudyUuid: string }>
    ) => {
      delete state.byCurrentStudy[action.payload.currentStudyUuid];
      if (state.latestStudyUuid === action.payload.currentStudyUuid) {
        state.latestStudyUuid = null;
      }
    },
  },
});

export const { comparisonReceived, comparisonCleared } = slice.actions;

export const selectLatestComparison = (
  state: RootState
): ComparisonTable | null => {
  const uuid = state.copilotComparisons.latestStudyUuid;
  return uuid ? (state.copilotComparisons.byCurrentStudy[uuid] ?? null) : null;
};

export default slice.reducer;
